import { useSyncExternalStore, useCallback } from 'react';

const isBrowser = typeof window !== 'undefined';

// Subscribes to browser navigation events
const subscribe = (callback: () => void) => {
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  if (!isBrowser) return () => {};
  window.addEventListener('popstate', callback);
  return () => window.removeEventListener('popstate', callback);
};

// Hook to get the current pathname (SSR-safe)
export const usePathname = (): string => {
  return useSyncExternalStore(
    subscribe,
    () => (isBrowser ? window.location.pathname : ''),
    () => '' // Server fallback
  );
};

// Hook to get the current search params (SSR-safe)
export const useSearchParams = (): URLSearchParams => {
  const search = useSyncExternalStore(
    subscribe,
    () => (isBrowser ? window.location.search : ''),
    () => '' // Server fallback
  );

  return new URLSearchParams(search);
};

// Hook to handle client-side navigation using the History API
export const useCustomRouter = () => {
  const navigate = useCallback((url: string, replace = false) => {
    if (!isBrowser) return;

    if (replace) {
      window.history.replaceState(null, '', url);
    } else {
      window.history.pushState(null, '', url);
    }

    // Notify all listeners that the URL has changed
    window.dispatchEvent(new PopStateEvent('popstate'));
  }, []);

  return { navigate };
};
